import React, { useState, useEffect } from 'react';
import '../pages/Landing.css';
import { useAuth } from '../contexts/AuthContext';
import { signInWithGoogle, handleRedirectResult, waitForAuthReady } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Check, Brain, MessageCircle, FileText, Cloud, BookOpen, Clock, User, Lock, Lightbulb, HelpCircle, Send, Linkedin, Youtube, Instagram } from 'lucide-react';
import { database } from '../supabase';
import Footer from '../components/Footer';
import LandingStudyShowcase from '../components/LandingStudyShowcase';



const Landing = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const navigate = useNavigate();
    const { user, authInitialized } = useAuth();


    useEffect(() => {
        console.log('🔍 useEffect triggered, user:', user);
        if (user) {
            console.log('🔍 User exists, UID:', user.uid);
            // alert(user.uid);

            const saveUserDoc = async () => {
                console.log('🔍 saveUserDoc function called - using Supabase');

                try {
                    // Check if user already exists in Supabase
                    const existingUser = await database.getUserByFirebaseUid(user.uid);
                    
                    if (existingUser) {
                        console.log('✅ User already exists in Supabase:', existingUser);
                        return;
                    }

                    console.log('🔍 Creating new user in Supabase...');
                    const newUser = await database.createUser(user);
                    console.log('✅ User created successfully in Supabase:', newUser);

                    // Create Stripe customer for the new user (skipped when billing is dormant)
                    try {
                        console.log('🔍 Creating Stripe customer...');
                        const stripeCustomerId = await database.createStripeCustomer(user);
                        if (stripeCustomerId) {
                            console.log('✅ Stripe customer created:', stripeCustomerId);
                            await database.updateUserStripeCustomerId(newUser.id, stripeCustomerId);
                            console.log('✅ User updated with Stripe customer ID');
                        }
                    } catch (stripeError) {
                        console.error('Error creating Stripe customer:', stripeError);
                        // Don't block user creation if Stripe fails
                    }

                    // alert('User created successfully in Supabase!');

                } catch (error) {
                    console.error('Error creating user in Supabase:', error);
                    alert('Error creating user: ' + error.message);
                }
            }

            saveUserDoc();
        }

    }, [user]);
    // alert(user.uid);

    const toggleModal = () => {
        setIsOpen(!isOpen);
    }  

    // Simple navigation for authenticated users
    useEffect(() => {
        if (authInitialized && user) {
            navigate(`/main/${user.uid}`);
        }
    }, [user, authInitialized, navigate]);

    const [userDocumentId, setUserDocumentId] = useState(null);

    const handleSignInWithGoogle = async () => {
        setAuthLoading(true);
        setAuthError(null);
        
        try {
            const result = await signInWithGoogle();
            if (result.success) {
                toggleModal();
                const saveUserDocumentId = async () => {
                    try {
                        // Check if user already exists in Supabase
                        const existingUser = await database.getUserByFirebaseUid(result.user.uid);
                        
                        if (existingUser) {
                            console.log('✅ User already exists in Supabase:', existingUser);
                            setUserDocumentId(existingUser.id);
                            return;
                        }

                        console.log('🔍 Creating new user in Supabase...');
                        const newUser = await database.createUser(result.user);
                        console.log('✅ User created successfully in Supabase:', newUser);
                        setUserDocumentId(newUser.id);

                        // Create Stripe customer for the new user (skipped when billing is dormant)
                        try {
                            console.log('🔍 Creating Stripe customer...');
                            const stripeCustomerId = await database.createStripeCustomer(result.user);
                            if (stripeCustomerId) {
                                console.log('✅ Stripe customer created:', stripeCustomerId);
                                await database.updateUserStripeCustomerId(newUser.id, stripeCustomerId);
                                console.log('✅ User updated with Stripe customer ID');
                            }
                        } catch (stripeError) {
                            console.error('Error creating Stripe customer:', stripeError);
                            // Don't block user creation if Stripe fails
                        }

                        // alert('User created successfully in Supabase!');
                        
                    } catch (error) {
                        console.error('❌ Failed to create user in Supabase:', error);
                        setUserDocumentId(null);
                    }
                }
                saveUserDocumentId();
            }
        } catch (error) {
            setAuthError(error.message);
        } finally {
            setAuthLoading(false);
        }
    };



    return (
        <div>
             <header className="header">
        <div className="header-container">
            <a href="#" className="logo">
                <div className="logo-icon"><img className='logo-icon-img' src="https://iili.io/KYpg7rg.png" alt="StudySpace" /></div>
                StudySpace
            </a>
            <nav className="nav">
                <a href="#features" className="nav-link">Features</a>
                <a href="/pricing" className="nav-link">Pricing</a>
                <a href="/mission" className="nav-link">Our Mission</a>
            </nav>
                <a href="#" className="sign-in-btn" onClick={toggleModal}>Sign in</a>
        </div>
    </header>

    <main className="main">
        <div className="illustration paper-plane"></div>
        <div className="illustration document"></div>
        <div className="illustration brain"></div>
        <div className="illustration quiz"></div>
        <div className="illustration formula">∫ f(x)dx</div>

        <div className="hero">
            {/* <div className="badge">
                <div className="badge-icon">AI</div>
                Powered by Advanced AI
            </div> */}
            
            <h1>Your AI-Powered Study Companion</h1>
            
            <p>
                Transform PDFs into quizzes and flashcards instantly. 
                Upload, study, succeed.
            </p>
            
            <a href="#" className="cta-btn" onClick={toggleModal}>Try for free</a>
        </div>

        <LandingStudyShowcase />

        {/* Why Choose StudySpace Section */}
        <div className="why-choose-container">
            <h2 className="why-choose-title">Why choose <span className="studyspace-gradient">StudySpace</span></h2>
            <p className="why-choose-subtitle">Discover what makes our AI study assistant the perfect companion for your learning journey</p>
            
            <div className="why-choose-grid">
                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <Brain className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">AI-Powered Analysis</h3>
                    <p className="why-choose-card-description">Deep understanding of your documents with advanced AI</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <MessageCircle className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Instant Q&A</h3>
                    <p className="why-choose-card-description">Get answers to any question about your study materials</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <FileText className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Auto Quiz Generation</h3>
                    <p className="why-choose-card-description">Practice tests created automatically from your content</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <Cloud className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Multiple File Types</h3>
                    <p className="why-choose-card-description">Support for PDF, DOC, DOCX, and PPTX files</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <BookOpen className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Smart Summaries</h3>
                    <p className="why-choose-card-description">Key concepts extracted and organized automatically</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <Clock className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">24/7 Availability</h3>
                    <p className="why-choose-card-description">Study anytime, anywhere with your AI tutor</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <User className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Personalized Learning</h3>
                    <p className="why-choose-card-description">Adapts to your learning style and pace</p>
                </div>

                <div className="why-choose-card">
                    <div className="why-choose-icon-container">
                        <Lock className="why-choose-icon" />
                    </div>
                    <h3 className="why-choose-card-title">Secure & Private</h3>
                    <p className="why-choose-card-description">Your documents and data are always protected</p>
                </div>
            </div>
        </div>

        {/* Call to Action Banner */}
        <div className="cta-banner-container">
            <div className="cta-banner">
                <h2 className="cta-banner-title">Study smarter, not harder. Let technology work for you.</h2>
                <button className="cta-banner-button" onClick={toggleModal}>
                    Try for free
                </button>
            </div>
        </div>

        {/* Interactive Study Guide Section */}
        {/* <div className="study-guide-container">
            <h2 className="study-guide-title">Transform documents into <span className="highlight">interactive study guides</span></h2>
            <p className="study-guide-subtitle">Upload any document and watch our AI create personalized study materials with Smart Quizzes</p>
            
            <div className="study-guide-content">
                <div className="study-material-panel">
                    <h3 className="material-title">Healthy Eating Basics Chapter 2</h3>
                    
                    <div className="overview-section">
                        <h4 className="section-title">Overview</h4>
                        <p className="overview-text">
                            Healthy eating is about making smart food choices that provide your body with the nutrients it needs to function properly. A balanced diet includes a variety of foods from all food groups, focusing on whole grains, lean proteins, fruits, and vegetables while limiting processed foods and added sugars.
                        </p>
                    </div>

                    <div className="key-concepts-section">
                        <h4 className="section-title">Key Concepts</h4>
                        <ul className="concepts-list">
                            <li><strong>Macronutrients:</strong> Proteins, carbs, and fats your body needs daily</li>
                            <li><strong>Micronutrients:</strong> Vitamins and minerals for optimal health</li>
                            <li><strong>Portion Control:</strong> Eating the right amount for your body</li>
                        </ul>
                    </div>

                    <div className="applications-section">
                        <h4 className="section-title">Applications</h4>
                        <div className="application-tags">
                            <span className="tag">Meal Planning</span>
                            <span className="tag">Weight Management</span>
                            <span className="tag">Energy Levels</span>
                        </div>
                    </div>

                    <div className="status-bar">
                        <Clock className="status-icon" />
                        <span>Generating study materials...</span>
                        <span className="time-estimate">10-15 seconds</span>
                    </div>
                </div>

                <div className="interactive-panel">
                    <div className="smart-explanation-card">
                        <div className="explanation-header">
                            <div className="explanation-icon">
                                <Lightbulb className="icon" />
                            </div>
                            <h4 className="explanation-title">Smart Explanation</h4>
                        </div>
                        <p className="explanation-text">
                            Macronutrients are the three main nutrients your body needs in large amounts. Think of them as your body's fuel - proteins build and repair muscles, carbohydrates give you energy, and fats help absorb vitamins and keep you feeling full. It's like having the right ingredients for a recipe!
                        </p>
                    </div>

                    <div className="quiz-card">
                        <div className="quiz-header">
                            <div className="quiz-icon">
                                <HelpCircle className="icon" />
                            </div>
                            <h4 className="quiz-title">Quick Quiz</h4>
                        </div>
                        
                        <div className="quiz-question">
                            <p>Which nutrient is primarily responsible for building and repairing muscles in your body?</p>
                        </div>

                        <div className="quiz-options">
                            <div className="option">
                                <span className="option-letter">A)</span>
                                <span>Carbohydrates</span>
                            </div>
                            <div className="option">
                                <span className="option-letter">B)</span>
                                <span>Fats</span>
                            </div>
                            <div className="option correct">
                                <span className="option-letter">C)</span>
                                <span>Proteins</span>
                                <Check className="check-icon" />
                            </div>
                            <div className="option">
                                <span className="option-letter">D)</span>
                                <span>Vitamins</span>
                            </div>
                        </div>

                        <div className="quiz-feedback">
                            <Check className="feedback-icon" />
                            <span>Correct! Great job!</span>
                        </div>

                        <div className="quiz-progress">
                            <span>Question 1 of 5</span>
                        </div>

                        <div className="quiz-actions">
                            <button className="continue-btn">Continue Quiz →</button>
                            <button className="review-btn">Review Notes</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="study-guide-cta">
                <button className="try-studyspace-btn" onClick={toggleModal}>Try StudySpace Free</button>
            </div>
        </div> */}
    </main>

    {/* Footer Section */}
    <footer className="footer">
        <div className="footer-container">
            <div className="footer-left">
                <div className="footer-logo">
                    <div className="footer-logo-icon">S</div>
                    <span className="footer-brand">StudySpace</span>
                </div>
                <p className="footer-copyright">© 2025 StudySpace, Boston, MA</p>
                
                {/* <div className="social-links">
                    <a href="#" className="social-link">
                        <Send className="social-icon" />
                    </a>
                    <a href="#" className="social-link">
                        <Linkedin className="social-icon" />
                    </a>
                    <a href="#" className="social-link">
                        <Youtube className="social-icon" />
                    </a>
                    <a href="#" className="social-link">
                        <Instagram className="social-icon" />
                    </a>
                </div> */}
                
                <div className="footer-status">
                    <Check className="status-icon" />
                    <span>All services are online.</span>
                </div>
            </div>
            
            <div className="footer-right">
                <div className="footer-columns">
                    <div className="footer-column">
                        <a href="/" className="footer-link">Features</a>
                        <a href="/pricing" className="footer-link">Pricing</a>
                        {/* <a href="#" className="footer-link">Blog</a> */}
                        <a href="/mission" className="footer-link">Our Mission</a>
                    </div>
                    {/* <div className="footer-column">
                        <a href="#" className="footer-link">Privacy Policy</a>
                        <a href="#" className="footer-link">Terms of Service</a>
                        <a href="#" className="footer-link">Cookie Policy</a>
                    </div> */}
                </div>
            </div>
        </div>
    </footer>
{/* 
    <Footer
    /> */}

    {isOpen && (
        <div className="modal">
            <div className="modal-content">
                <button className="modal-close" onClick={toggleModal}>×</button>
                
                <h2 className="modal-title">Welcome to StudySpace</h2>
                
                <button onClick={handleSignInWithGoogle} className="google-btn" disabled={authLoading}>
                    <div className="google-logo">G</div>
                    {authLoading ? 'Signing in...' : 'Continue with Google'}
                </button>
                
                {authError && (
                    <div className="auth-error">
                        {authError}
                    </div>
                )}
{/*                 
                <div className="separator">
                    <span>or</span>
                </div>
                
                <input type="email" className="email-input" placeholder="EMAIL" />
                 */}
                {/* <button className="email-btn">Continue with email</button> */}
                
                <p className="login-link">
                    Have an Account? <a href="#" onClick={toggleModal}>Log In</a>
                </p>
            </div>
        </div>
    )}
        </div>
    )
}

export default Landing;